import { Model } from 'app/model/backendmodel';
import {
    IAzureResource,
    IAzureResourceCreateResult,
    IAzureResourceShadow,
    IDataSourceCredentialUrlResponse,
    ISOCDataLakeAnalyticsAccount
} from 'app/interfaces/IAzureResource';
import { FactoryUrlInformationService } from '../factory-url-information.service';
import { LoggerService } from 'app/logger/logger.service';
import { IMetadataClient } from '../base-metadata-client';
import { IFrameMessengerService } from '../iframe-messenger.service';
import { notImplementedFunction } from 'app/extension/api/common';
import { Trigger } from 'app/model/trigger/trigger';
import { AuthConstants } from '../authentication/auth-constants';
import { ResourceType } from '../globals';
import { ResponseOptions } from '../http/responseOptions';
import { Headers, Response } from 'app/services/http';
import { AdapterContext, format, LoadingStatusEnum, type ITridentResource } from 'shell-models-lib';
import { DEDICATED_NOTFOUND_ENTITY_ERROR_TEMPLATE } from '../iframe-customer-profiles';
import {
    convertDataFactoryResourceToTridentResource,
    convertTridentResourceToAzureResource,
    isSupportedTridentResource
} from '../iframe/trident-resource-adapter';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';
import * as Constants from 'app/services/globals';

export class TridentIFrameMetadataClient implements IMetadataClient {
    public className = 'TridentIFrameMetadataClient';
    protected authArea: string = AuthConstants.AUTH_AREA;
    private readonly _adapterContext: AdapterContext = AdapterContext.defaultContextOf(this._contextData);
    private readonly _getResourceMapCache: Map<string, IAzureResource> = new Map();
    private readonly _listResourceMapCache: Map<string, IAzureResource[]> = new Map();

    constructor(
        private readonly _contextData: IResourceContext,
        private readonly _urlService: FactoryUrlInformationService,
        private readonly _logger: LoggerService,
        readonly iframeMessengerService: IFrameMessengerService
    ) {}

    public async getResource(
        resourceType?: Constants.ResourceType,
        resourceIdentifier?: string,
        omitErrorLog?: boolean,
        options?: { workspaceObjectId?: string; resourceSubType?: string; shouldCache?: boolean; force?: boolean, skipValidation?: boolean }
    ): Promise<IAzureResource> {
        const { workspaceObjectId, resourceSubType, shouldCache, force, skipValidation } = options ?? {
            force: true,
            shouldCache: false
        };
        if (!force && this._getResourceMapCache.get(resourceIdentifier)) {
            return this._getResourceMapCache.get(resourceIdentifier);
        }
        const tridentResource = (await this.iframeMessengerService.getResourceAsync({
            contextData: this._contextData,
            workspaceObjectId: workspaceObjectId ?? this._contextData.contextId,
            resourceType,
            resourceObjectID: resourceIdentifier,
            resourceSubType
        })) as ITridentResource;
        if (!tridentResource || (!skipValidation && !(await isSupportedTridentResource(tridentResource, this._contextData)))) {
            throw new Error(format(DEDICATED_NOTFOUND_ENTITY_ERROR_TEMPLATE, resourceIdentifier));
        }
        const result = await convertTridentResourceToAzureResource(tridentResource, {
            logger: this._logger,
            adapterContext: this._adapterContext
        });
        if (shouldCache) {
            this._getResourceMapCache.set(resourceIdentifier, result);
        }
        return result;
    }

    public async update(resourceType: ResourceType, name: string, payload: IAzureResource): Promise<IAzureResource> {
        const result = await this.iframeMessengerService.saveResourceInTridentAsync(
            this._contextData,
            convertDataFactoryResourceToTridentResource(payload),
            resourceType,
            'create'
        );
        if (result.error) {
            throw result.error;
        }
        return convertTridentResourceToAzureResource(result.publishedArtifact, {
            logger: this._logger,
            adapterContext: this._adapterContext
        });
    }

    public async rename(resourceType: ResourceType, name: string, payload: {}): Promise<Response> {
        return notImplementedFunction();
    }

    public async patch(resourceType: ResourceType, name: string, payload: IAzureResource): Promise<IAzureResource> {
        const result = await this.iframeMessengerService.saveResourceInTridentAsync(
            this._contextData,
            convertDataFactoryResourceToTridentResource(payload),
            resourceType,
            'update'
        );
        if (result.error) {
            throw result.error;
        }
        return convertTridentResourceToAzureResource(result.publishedArtifact, {
            logger: this._logger,
            adapterContext: this._adapterContext
        });
    }

    public async create(resourceType: ResourceType, name: string, payload: IAzureResource): Promise<IAzureResource> {
        const result = await this.iframeMessengerService.saveResourceInTridentAsync(
            this._contextData,
            convertDataFactoryResourceToTridentResource(payload),
            resourceType,
            'create'
        );
        if (result.error) {
            throw result.error;
        }
        return convertTridentResourceToAzureResource(result.publishedArtifact, {
            logger: this._logger,
            adapterContext: this._adapterContext
        });
    }

    public async createWithRequestId(resourceType: ResourceType, name: string, payload: IAzureResource): Promise<IAzureResourceCreateResult> {
        const result = await this.iframeMessengerService.saveResourceInTridentAsync(
            this._contextData,
            convertDataFactoryResourceToTridentResource(payload),
            resourceType,
            'create'
        );
        if (result.error) {
            result.error.requestId = result.requestId;
            throw result.error;
        }
        const resource = await convertTridentResourceToAzureResource(result.publishedArtifact, {
            logger: this._logger,
            adapterContext: this._adapterContext
        });
        return { resource, requestId: result.requestId };
    }

    public async patchWithRequestId(resourceType: ResourceType, name: string, payload: IAzureResource): Promise<IAzureResourceCreateResult> {
        const result = await this.iframeMessengerService.saveResourceInTridentAsync(
            this._contextData,
            convertDataFactoryResourceToTridentResource(payload),
            resourceType,
            'update'
        );
        if (result.error) {
            result.error.requestId = result.requestId;
            throw result.error;
        }
        const resource = await convertTridentResourceToAzureResource(result.publishedArtifact, {
            logger: this._logger,
            adapterContext: this._adapterContext
        });
        return { resource, requestId: result.requestId };
    }

    public async deleteResource(
        resourceType: ResourceType,
        resourceName: string,
        retryBadRequest: boolean = false
    ): Promise<Response> {
        const iframeData = await this.iframeMessengerService
            .deleteResourceAsync({
                artifactName: resourceName,
                artifactType: resourceType,
                objectId: resourceName
            })
            .then((val) => ({
                ...val,
                headers: new Headers(val.headers)
            }));
        return new Response(new ResponseOptions(iframeData));
    }

    public getSparkHistoryEndpoint(baseUri: string): Promise<string> {
        return notImplementedFunction();
    }

    public getSparkHistoryServerVersion(workspace: Model.Workspace): Promise<string> {
        return notImplementedFunction();
    }

    public getSOCDataLakeAnalyticsAccount(workspace: Model.Workspace): Promise<ISOCDataLakeAnalyticsAccount> {
        return notImplementedFunction();
    }

    public async listResources(
        resourceType: Constants.ResourceType,
        complexResourceType?: string,
        apiVersion?: string,
        options?: { workspaceObjectId?: string; shouldCache?: boolean; force?: boolean, dontValidate?: boolean, convertToFabric?: boolean }
    ): Promise<IAzureResource[]> {
        const workspaceObjectId = options?.workspaceObjectId ?? this._contextData.contextId;
        const { force, shouldCache } = options ?? { force: true, shouldCache: false };
        const cachedKey = `${workspaceObjectId}/${resourceType}`;
        if (!force && this._listResourceMapCache.get(cachedKey)) {
            return this._listResourceMapCache.get(cachedKey);
        }
        const tridentResources = await this.iframeMessengerService.listResourcesAsync({
            contextData: this._contextData,
            workspaceObjectId: options?.workspaceObjectId ?? this._contextData.contextId,
            resourceType,
            forceRefresh: force
        });
        this._adapterContext.adapterConfig.convertToFabric = options?.convertToFabric ?? false;
        const result = await Promise.all(
            (tridentResources as ITridentResource[])
                .map(async (item) => {
                    return (options?.dontValidate || await isSupportedTridentResource(item, this._contextData))
                        ? await convertTridentResourceToAzureResource(item, {
                              logger: this._logger,
                              adapterContext: this._adapterContext
                          })
                        : undefined;
                })
        ).then((resources) => resources.filter(Boolean));
        if (shouldCache) {
            this._listResourceMapCache.set(cachedKey, result);
        }
        return result;
    }

    public async listResourceShadows(
        resourceType: Constants.ResourceType,
        options?: { workspaceObjectId?: string }
    ): Promise<IAzureResourceShadow[]> {
        const tridentResources = await this.iframeMessengerService.listResourcesAsync({
            contextData: this._contextData,
            workspaceObjectId: options?.workspaceObjectId ?? this._contextData.contextId,
            resourceType,
            shallowGet: true
        });
        return Promise.all(
            (tridentResources as ITridentResource[])
                .map(async (item) => {
                    const result = (await isSupportedTridentResource(item, this._contextData))
                        ? await convertTridentResourceToAzureResource(item, {
                              logger: this._logger,
                              adapterContext: this._adapterContext
                          })
                        : undefined;
                    if (result && item.resourceLoadingStatus === 'Stub') {
                        result.status = LoadingStatusEnum.Stub;
                    }
                    return result;
                })
        ).then((resources) => resources.filter(Boolean));
    }

    public async getDataSourceCredentialUrl(lsName: string): Promise<IDataSourceCredentialUrlResponse> {
        return notImplementedFunction();
    }

    public async getFirstPage(...args: Parameters<IMetadataClient['getFirstPage']>): Promise<IAzureResource[]> {
        return !args[3]?.shallowGet
            ? this.listResources(args[0], undefined, undefined, args[3])
            : this.listResourceShadows(args[0], args[3]);
    }

    public async getNextPage(nextLink: string): Promise<Response> {
        return notImplementedFunction();
    }

    public getUAMI(): Promise<unknown> {
        return notImplementedFunction();
    }

    public async queryTriggers(triggerFilter: Model.TriggerQueryFilter): Promise<Trigger[]> {
        return notImplementedFunction();
    }

    public getAuthArea(): string {
        return this.authArea;
    }

    public getBaseUri(): string {
        return `${AuthConstants.RESOURCE_MANAGER_ENDPOINT}${this._urlService.getResourceId()}`;
    }

    public async upgradeLinkedService(linkedServiceName: string): Promise<Response> {
        return notImplementedFunction();
    }
}
